import { RouteComponentProps, Redirect } from "react-router";
import React from 'react';
import QuestionnaireContext from "contexts/QuestionnaireContext";
import { Button, Container, Row, Col, Collapse } from "react-bootstrap";
import l10n from "localization/LocalizationData";
import { ValueResource } from "models/QuestionnaireResource";
import { QuestionField } from "components/common/QuestionField";
import queryString from 'query-string';
import CategoryDescriptionView from "./CategoryDescriptionView";
import { ImageShower } from "components/controls/ImageShower";
import { HelpModal } from "components/modals/HelpModal";
import { hideHeader } from "Utilities/QueryParamSettings";

interface QuestionProps {
	index: string;
}

export const QuestionView = (props: RouteComponentProps<QuestionProps, {}>) => {
	const context = React.useContext(QuestionnaireContext);
	const [showHelpModal, setshowHelpModal] = React.useState(false);
	const [invalidAnswer, setInvalidAnswer] = React.useState<null | 'primary' | 'secondary'>(null);
	const [smallDevice, setSmallDevice] = React.useState<boolean>(window.matchMedia('(max-width: 540px)').matches);
	const queryHint = queryString.parse(props.location.search)['hint'] !== undefined;
	const headerless = hideHeader();

	React.useEffect(() => {
		if (context.questionnaire && `${props.location.pathname}${props.location.search}` !== context.lastState) {
			context.setLastState(`${props.location.pathname}${props.location.search}`);
			setInvalidAnswer(null);
		}
	}, [context, props.location.pathname, props.location.search]);

	React.useEffect(() => {
		let element: any = undefined;
		if (context.questionnaire) {
			if (!context.questionnaire.description || queryHint) {
				element = undefined;
			} else if (context.questionnaire.description && !headerless && !smallDevice) {
				element = <Button className='button-info mr-4' onClick={() => setshowHelpModal(true)}>{l10n.button_instructions}</Button>;
			}
		}
		if ((element !== undefined && context.headerElement === undefined) ||
			(element === undefined && context.headerElement !== undefined)) {
			context.setHeaderElement(element);
		}
	}, [context, smallDevice, headerless, queryHint])

	React.useEffect(() => {
		function checkSize() {
			const match = window.matchMedia('(max-width: 540px)');
			if (match.matches !== smallDevice) {
				setSmallDevice(match.matches);
			}
		}
		window.addEventListener("resize", checkSize);
		return (() => {
			window.removeEventListener("resize", checkSize);
		})
	}, [smallDevice]);

	if (context.questionnaire === null) {
		return <Redirect to='/questionnaire/error' />
	}

	const questionnaire = context.questionnaire;

	const questionCount = questionnaire.questions.length;

	const index = parseInt(props.match.params.index);
	if (isNaN(index) || index >= context.questionnaire.questions.length) {
		return <Redirect to={`/questionnaire/${questionnaire.id}/question/0`} />
	}

	const question = questionnaire.questions[index];
	const category = question.category;

	if (queryHint && category.description === undefined) {
		return <Redirect to={props.location.pathname} />
	}

	const primaryValues = category.values?.primary as ValueResource;
	const secondaryValues = category.values?.secondary;
	const answer = context.answers[question.id];

	const hasQuestionnaireHint = questionnaire.description !== undefined;
	const hasCategoryHint = category.description !== undefined;

	function navigateNext() {
		if (queryHint === false && !answerValid()) {
			return;
		}
		if (index + 1 === context.questionnaire?.questions.length) {
			props.history.push(`/questionnaire/${context.questionnaire?.id}/preliminary`);
		} else {
			const nextQuestion = questionnaire.questions[index + 1];

			if (nextQuestion.category.id !== question.category.id && nextQuestion.category.description !== undefined) {
				props.history.push(`/questionnaire/${context.questionnaire?.id}/question/${index + 1}?hint`);
			} else {
				if (queryHint) {
					props.history.push(`/questionnaire/${context.questionnaire?.id}/question/${index}`);
				} else {
					props.history.push(`/questionnaire/${context.questionnaire?.id}/question/${index + 1}`);
				}
			}
		}
	}

	function navigateBack() {
		if (index === 0 || (questionnaire.questions[index - 1].category.id !== category.id && !queryHint)) {
			props.history.push(`/questionnaire/${context.questionnaire?.id}/question/${index}?hint`);
		} else {
			props.history.push(`/questionnaire/${context.questionnaire?.id}/question/${index - 1}`);
		}
	}

	function toSummary() {
		props.history.push(`/questionnaire/${context.questionnaire?.id}/summary`);
	}

	function answerChanged(primary: number | null, secondary: number | null) {
		if (primary !== null) {
			if (answer?.primary !== primary) {
				context.setPrimaryAnswer(question, primary);
				if (invalidAnswer === 'primary') {
					setInvalidAnswer(null);
				}
			}
		} else if (secondary !== null) {
			if (answer?.secondary !== secondary) {
				context.setSecondaryAnswer(question, secondary);
				if (invalidAnswer === 'secondary') {
					setInvalidAnswer(null);
				}
			}
		}
	}


	function answerValid(): boolean {

		if (!context.answers[question.id]) {
			setInvalidAnswer('primary');
			return false;
		}
		if (context.answers[question.id].primary === undefined) {
			setInvalidAnswer('primary');
			return false;
		}
		if (context.answers[question.id].secondary === undefined) {
			setInvalidAnswer('secondary');
			return false;
		}

		return true;
	}

	if (hasCategoryHint && queryHint) {
		return (
			<div className='question-container'>
				<CategoryDescriptionView category={category}>
					<Row className='justify-content-center'>
						<Col xs='auto' className='d-flex justify-content-end'>
							<Button className='float-left' disabled={index === 0} onClick={navigateBack}>{l10n.button_previous}</Button>
						</Col>
						<Col xs='auto' className='d-flex justify-content-start'>
							<Button className='float-right' onClick={navigateNext}>{l10n.button_next}</Button>
						</Col>
					</Row>
				</CategoryDescriptionView>
			</div >
		)
	}

	const questionHintParts = question.hint?.split("<c>");

	return (
		<>
			<div className='question-container'>
				{
					hasQuestionnaireHint &&
					<HelpModal content={questionnaire.description as string} hide={() => setshowHelpModal(false)} show={showHelpModal} title={l10n.question_help_title} />
				}
				<Container className='pt-4'>
					<h1 className='mb-4 text-center'>{question.title}</h1>
					<Row className='justify-content-center text-center'>
						<Col xs='auto' lg='8'>
							<p className='question-description'>
								{
									questionHintParts && questionHintParts.map((value, index) => {
										if (index % 2 === 1)
											return <i key={"hint" + index}>{value}</i>;
										return value;
									})
								}
							</p>
						</Col>
					</Row>
					{
						question.imageurl &&
						<Row className='justify-content-center mb-4'>
							<Col xs='auto'>
								<ImageShower url={question.imageurl} alt={question.title} />
							</Col>
						</Row>
					}
					<Row className='justify-content-center'>
						<Col xs='12' className='primary-answer-col'>
							<QuestionField idPrefix={question.id} currentValue={answer?.primary}
								answerValue={primaryValues}
								valueChanged={(newVal) => answerChanged(newVal, null)}
								title={category.questiontitle} />
						</Col>
					</Row>
					{
						secondaryValues &&
						<React.Fragment>
							<Row className='justify-content-center text-center mt-4'>
								<p className='text-center'>{secondaryValues.title}</p>


							</Row>
							<Row className='justify-content-center'>
								<Col xs='12' className='d-flex flex-row justify-content-center'>

									<QuestionField currentValue={answer?.secondary}
										answerValue={secondaryValues}
										valueChanged={(newVal) => answerChanged(null, newVal)}
										idPrefix={`${question.id}`} />

								</Col>
							</Row>
						</React.Fragment>

					}
					<Row className='invalid-answer-alert-container mt-4'>
						<Collapse in={invalidAnswer !== null}>
							<div>
								<p role='alert' className='question-invalid-answer-alert'><b>{l10n.question_answerQuestion_error}</b></p>
							</div>
						</Collapse>
					</Row>
					<div className='question-navigation-container'>
					
							<Button disabled={(!hasCategoryHint && index === 0) || (invalidAnswer !== null)} onClick={() => navigateBack()}>{l10n.button_previous} </Button>
						
						<div className='question-progress-text'>
							{`${index + 1} / ${questionCount}`}
						</div>
							<Button disabled={invalidAnswer !== null} onClick={() => navigateNext()}>{l10n.button_next}</Button>
						<div className='d-flex justify-content-center col-12 mt-2'>
							<Button disabled={context.canJumpToSummary === false}
								hidden={context.canJumpToSummary === false}
								onClick={() => toSummary()}>
								{l10n.button_toSummary}
							</Button>
						</div>
					</div>
				</Container >
			</div >
			{
				((headerless || smallDevice) && hasQuestionnaireHint) &&
				<div className='info-button-container'><Button className='button-info' onClick={() => setshowHelpModal(true)}>{l10n.button_instructions}</Button></div>

			}
		</>
	);
}